import {
  Component, ChangeDetectionStrategy, ElementRef, ViewChild, effect, inject, signal, computed
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogRequest, DialogService, DialogTone } from '../../../core/services/dialog.service';

/**
 * <app-dialog-host> — renders DialogService pop-ups (mounted once in app.component.html).
 * Canvas "Admin confirmations & results" board: rgba(16,33,31,.55) backdrop, white 20px card, 40px tinted
 * icon tile, 15.5px bold title, 13px body, [secondary] [primary] buttons on the right. Phones (≤600px): a
 * bottom sheet with equal-width 48px buttons and a 16px input (no iOS zoom).
 * Keyboard: Escape = cancel/close, Enter = OK (in a one-line field), Tab stays inside; focus returns after.
 */
const ICONS: Record<string, string> = {
  info:     'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
  question: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01',
  success:  'M22 11.1V12a10 10 0 1 1-5.9-9.1M22 4 12 14l-3-3',
  warning:  'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01',
  danger:   'M7.9 2h8.2L22 7.9v8.2L16.1 22H7.9L2 16.1V7.9ZM12 8v4M12 16h.01',
  prompt:   'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z',
};

@Component({
  selector: 'app-dialog-host',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (dlg(); as d) {
      <div class="dh-backdrop" (click)="onBackdrop($event)">
        <div class="dh-card" [class]="'dh-card tone-' + d.tone" #card
             [attr.role]="d.kind === 'alert' ? 'alertdialog' : 'dialog'" aria-modal="true"
             [attr.aria-labelledby]="'dh-title-' + d.id" [attr.aria-describedby]="d.message ? 'dh-body-' + d.id : null"
             (keydown)="onKeydown($event, d)">
          <div class="dh-head">
            <span class="dh-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path [attr.d]="iconPath()"></path></svg>
            </span>
            <h2 class="dh-title" [id]="'dh-title-' + d.id">{{ d.title }}</h2>
          </div>
          @if (d.message) {
            <div class="dh-body" [id]="'dh-body-' + d.id">{{ d.message }}</div>
          }
          @if (d.kind === 'prompt') {
            <label class="dh-field">
              @if (d.prompt?.label) { <span class="dh-label">{{ d.prompt?.label }}@if (d.prompt?.required) {<span class="dh-req"> *</span>}</span> }
              @if (d.prompt?.multiline) {
                <textarea #field class="dh-input dh-textarea" [value]="value()" (input)="value.set($any($event.target).value)"
                          [attr.placeholder]="d.prompt?.placeholder || null" [attr.maxlength]="d.prompt?.maxLength || null"
                          [attr.aria-label]="d.prompt?.label ? null : d.title"></textarea>
              } @else {
                <input #field class="dh-input" [type]="d.prompt?.inputType || 'text'" [value]="value()" (input)="value.set($any($event.target).value)"
                       [attr.placeholder]="d.prompt?.placeholder || null" [attr.maxlength]="d.prompt?.maxLength || null"
                       [attr.aria-label]="d.prompt?.label ? null : d.title">
              }
              @if (d.prompt?.maxLength) { <span class="dh-count">{{ value().length }} / {{ d.prompt?.maxLength }}</span> }
            </label>
          }
          <div class="dh-actions">
            @if (d.kind !== 'alert') {
              <button type="button" class="dh-btn dh-secondary" #cancelBtn (click)="cancel(d)">{{ d.cancelText }}</button>
            }
            <button type="button" class="dh-btn dh-primary" #okBtn (click)="ok(d)" [disabled]="!canSubmit()">{{ d.okText }}</button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }
    .dh-backdrop {
      position: fixed; inset: 0; z-index: 2147483000;   /* above every app layer (offline panel 100002, spinner, toasts) */
      background: rgba(16, 33, 31, 0.55);
      display: flex; align-items: center; justify-content: center; padding: 24px;
      animation: dh-fade .12s ease-out;
    }
    .dh-card {
      width: 100%; max-width: 440px; max-height: calc(100vh - 48px); max-height: calc(100dvh - 48px);
      box-sizing: border-box; overflow: auto;
      background: #FFFFFF; border-radius: 20px; padding: 20px;
      display: flex; flex-direction: column; gap: 12px;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
      font-family: var(--mt-font, 'Plus Jakarta Sans', system-ui, sans-serif); color: var(--mt-ink, #16201F);
      animation: dh-pop .14s ease-out;
      outline: none;
    }
    .dh-head { display: flex; gap: 12px; align-items: center; }
    .dh-icon {
      width: 40px; height: 40px; flex-shrink: 0; border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      background: #EAF5F2; color: #0E6B63;
    }
    .tone-success .dh-icon { background: #E4F5EA; color: #11603A; }
    .tone-warning .dh-icon { background: #FEF0C7; color: #93370D; }
    .tone-danger  .dh-icon { background: #FEE4E2; color: #B42318; }
    .dh-title { margin: 0; font-size: 15.5px; font-weight: 700; line-height: 1.35; overflow-wrap: anywhere; }
    .dh-body {
      font-size: 13.5px; line-height: 1.55; color: var(--mt-ink-2, #3A4644);
      white-space: pre-line; overflow-wrap: anywhere;
    }
    .dh-field { display: flex; flex-direction: column; gap: 6px; }
    .dh-label { font-size: 12.5px; font-weight: 600; color: var(--mt-ink-2, #3A4644); }
    .dh-req { color: #B42318; }
    .dh-input {
      height: 42px; padding: 0 12px; box-sizing: border-box; width: 100%;
      border: 1px solid #DDDBD3; border-radius: 10px; background: #FFFFFF;
      font-family: inherit; font-size: 14px; color: inherit;
    }
    .dh-textarea { height: 96px; padding: 10px 12px; resize: vertical; line-height: 1.45; }
    .dh-input:focus { outline: none; border-color: #0E6B63; box-shadow: 0 0 0 3px rgba(14, 107, 99, 0.14); }
    .dh-count { align-self: flex-end; font-size: 11.5px; color: var(--mt-muted, #5F6B69); }
    .dh-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 2px; }
    .dh-btn {
      height: 40px; padding: 0 16px; border-radius: 10px; cursor: pointer;
      font-family: inherit; font-size: 13.5px; font-weight: 700; white-space: nowrap;
    }
    .dh-secondary { border: 1px solid #DDDBD3; background: #FFFFFF; color: var(--mt-ink, #16201F); font-weight: 600; }
    .dh-secondary:hover { background: #F7F6F2; border-color: #5F6B69; }
    .dh-primary { border: 0; background: #0E6B63; color: #FFFFFF; }
    .dh-primary:hover:not(:disabled) { background: #0A4F49; }
    .tone-warning .dh-primary { background: #B45309; }
    .tone-warning .dh-primary:hover:not(:disabled) { background: #8F4207; }
    .tone-danger  .dh-primary { background: #B42318; }
    .tone-danger  .dh-primary:hover:not(:disabled) { background: #8E1B12; }
    .dh-primary:disabled { opacity: .45; cursor: not-allowed; }
    .dh-btn:focus-visible { outline: 3px solid rgba(14, 107, 99, 0.35); outline-offset: 2px; }

    @media (max-width: 600px) {
      .dh-backdrop { align-items: flex-end; padding: 0; }
      .dh-card {
        max-width: none; border-radius: 20px 20px 0 0;
        padding: 20px 16px calc(16px + env(safe-area-inset-bottom, 0px));
        max-height: 88vh; max-height: 88dvh;
        animation: dh-rise .16s ease-out;
      }
      .dh-actions { gap: 10px; }
      .dh-btn { flex: 1; height: 48px; font-size: 15px; }
      .dh-input { height: 48px; font-size: 16px; }
      .dh-textarea { height: 110px; }
    }
    @keyframes dh-fade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes dh-pop  { from { opacity: 0; transform: translateY(6px) scale(.98); } to { opacity: 1; transform: none; } }
    @keyframes dh-rise { from { transform: translateY(24px); opacity: .6; } to { transform: none; opacity: 1; } }
    @media (prefers-reduced-motion: reduce) { .dh-backdrop, .dh-card { animation: none; } }
  `],
})
export class DialogHostComponent {
  private readonly backCancels = inject(Router, { optional: true })?.events.pipe(takeUntilDestroyed()).subscribe(e => {
    if (e instanceof NavigationStart && e.navigationTrigger === 'popstate') this.dialogs.cancelAll();
  });
  private readonly dialogs = inject(DialogService);
  readonly dlg = this.dialogs.current;
  readonly value = signal('');

  @ViewChild('card') card?: ElementRef<HTMLElement>;
  @ViewChild('field') field?: ElementRef<HTMLInputElement | HTMLTextAreaElement>;
  @ViewChild('okBtn') okBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('cancelBtn') cancelBtn?: ElementRef<HTMLButtonElement>;

  private returnFocus: HTMLElement | null = null;

  readonly iconPath = computed(() => {
    const d = this.dlg();
    if (!d) return '';
    if (d.kind === 'prompt') return ICONS['prompt'];
    if (d.kind === 'confirm' && d.tone === 'info') return ICONS['question'];
    return ICONS[d.tone as DialogTone] ?? ICONS['info'];
  });

  readonly canSubmit = computed(() => {
    const d = this.dlg();
    return !(d?.kind === 'prompt' && d.prompt?.required && !this.value().trim());
  });

  constructor() {
    effect(() => {
      const d = this.dlg();
      if (!d) {
        const back = this.returnFocus;
        this.returnFocus = null;
        if (back && document.contains(back)) setTimeout(() => back.focus(), 0);
        return;
      }
      if (!this.returnFocus && document.activeElement instanceof HTMLElement) this.returnFocus = document.activeElement;
      this.value.set(d.prompt?.defaultValue ?? '');
      // Focus once the dialog is drawn: the field for a prompt, Cancel for a red (destructive) confirm, else OK.
      setTimeout(() => {
        const target = d.kind === 'prompt' ? this.field
          : d.kind === 'confirm' && d.tone === 'danger' ? this.cancelBtn : this.okBtn;
        target?.nativeElement.focus();
        if (d.kind === 'prompt') this.field?.nativeElement.select?.();
      }, 0);
    });
  }

  ok(d: DialogRequest): void {
    if (!this.canSubmit()) return;
    this.dialogs.close(d.kind === 'alert' ? undefined : d.kind === 'confirm' ? true : this.value());
  }

  cancel(d: DialogRequest): void {
    this.dialogs.close(d.kind === 'alert' ? undefined : d.kind === 'confirm' ? false : null);
  }

  onBackdrop(e: MouseEvent): void {
    const d = this.dlg();
    if (d && e.target === e.currentTarget) this.cancel(d);
  }

  onKeydown(e: KeyboardEvent, d: DialogRequest): void {
    if (e.key === 'Escape') { e.preventDefault(); this.cancel(d); return; }
    if (e.key === 'Enter' && d.kind === 'prompt' && !d.prompt?.multiline && e.target === this.field?.nativeElement) {
      e.preventDefault(); this.ok(d); return;
    }
    if (e.key === 'Tab' && this.card) {
      const items = Array.from(this.card.nativeElement.querySelectorAll<HTMLElement>('button:not([disabled]), input, textarea'));
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
}
